class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = FacetFiltersForm.sectionUrl(section.section, searchParams);
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  // Collection tabs (see snippets/facets.liquid) swap another collection's grid into this
  // page, so while a tab is active the filters and sort must query THAT collection. Every
  // collection template keys this section the same, so `?section_id=` still works on the
  // tab's path; `fullPage` is the tab script's escape hatch for one that doesn't.
  static sectionUrl(sectionId, searchParams) {
    const tabs = window.collectionTabs;
    if (tabs && tabs.path) {
      if (tabs.fullPage) return `${tabs.path}${searchParams ? `?${searchParams}` : ''}`;
      return `${tabs.path}?section_id=${sectionId}&${searchParams}`;
    }
    return `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    // Lets the collection tabs re-stamp the swapped-in grid (it carries the tab
    // collection's section id) and reset their page counter — filtering means page 1.
    if (window.collectionTabs && window.collectionTabs.afterRender) window.collectionTabs.afterRender();

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    // The active collection tab has to survive a filter/sort change, otherwise the next
    // reload or pagination click drops back to "All".
    const tabParams = window.collectionTabs && window.collectionTabs.urlParams ? window.collectionTabs.urlParams() : '';
    const query = [searchParams, tabParams].filter(Boolean).join('&');
    history.pushState({ searchParams }, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Minimal Shopify money formatter — mirrors the shop's money_format string so the
// slider's live labels match the values rendered by Liquid's `money` filter.
function formatMoneyFromFormat(amount, format) {
  format = format || '{{amount}}';
  const num = (value, precision, thousands, decimal) => {
    const parts = (Number(value) || 0).toFixed(precision).split('.');
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  };
  return format
    .replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return num(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return num(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return num(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return num(amount, 2, "'", '.');
        case 'amount_with_space_separator':
          return num(amount, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return num(amount, 0, ' ', ',');
        default:
          return num(amount, 2, ',', '.');
      }
    })
    .replace(/<[^>]*>/g, '');
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    // Scope to the text inputs only — the slider's range handles are excluded.
    this.querySelectorAll('.field__input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    this.initSlider();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncSliderFromInputs();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('.field__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    // An empty field means "no bound" — leave it empty, don't clamp it to data-min.
    if (input.value === '') return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  // ── Slider ─────────────────────────────────────────────
  initSlider() {
    this.facet = this.querySelector('[data-price-facet]');
    if (!this.facet) return;

    const inputs = this.querySelectorAll('.field__input');
    this.minInput = inputs[0];
    this.maxInput = inputs[1];
    this.minHandle = this.facet.querySelector('[data-price-handle="min"]');
    this.maxHandle = this.facet.querySelector('[data-price-handle="max"]');
    this.progress = this.facet.querySelector('[data-price-progress]');
    this.minLabel = this.facet.querySelector('[data-price-min-label]');
    this.maxLabel = this.facet.querySelector('[data-price-max-label]');
    if (!this.minHandle || !this.maxHandle) return;

    this.sliderMax = parseFloat(this.facet.dataset.sliderMax) || 0;
    this.moneyFormat = this.facet.dataset.moneyFormat || '{{amount}}';

    this.minHandle.addEventListener('input', this.onSliderInput.bind(this, 'min'));
    this.maxHandle.addEventListener('input', this.onSliderInput.bind(this, 'max'));
    this.minHandle.addEventListener('change', this.commitSlider.bind(this));
    this.maxHandle.addEventListener('change', this.commitSlider.bind(this));

    this.paint(parseFloat(this.minHandle.value) || 0, parseFloat(this.maxHandle.value) || this.sliderMax);
  }

  onSliderInput(which, event) {
    // Don't let drag events reach the form's debounced submit — we only commit
    // (and re-filter) on release, so results aren't fetched with stale values.
    if (event) event.stopPropagation();
    let minV = parseFloat(this.minHandle.value);
    let maxV = parseFloat(this.maxHandle.value);
    // Prevent the handles from crossing.
    if (minV > maxV) {
      if (which === 'min') this.minHandle.value = maxV;
      else this.maxHandle.value = minV;
      minV = parseFloat(this.minHandle.value);
      maxV = parseFloat(this.maxHandle.value);
    }
    this.paint(minV, maxV);
  }

  // Fired on handle release — write the values into the real inputs and notify the
  // form so results re-filter. We dispatch ONLY `input` (what the form listens to):
  // the slider already produced valid values, so re-running the change-driven
  // adjustToValidValues would wrongly clamp an empty (unbounded) field to the other
  // handle's value and collapse both handles together. Keep the data-attr links
  // fresh directly instead.
  commitSlider() {
    const minV = parseFloat(this.minHandle.value);
    const maxV = parseFloat(this.maxHandle.value);
    this.minInput.value = minV > 0 ? minV : '';
    this.maxInput.value = maxV < this.sliderMax ? maxV : '';
    this.setMinAndMaxValues();
    this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.maxInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // Keep the handles aligned when the number boxes are edited directly.
  syncSliderFromInputs() {
    if (!this.minHandle || !this.maxHandle) return;
    const parse = (raw, fallback) => {
      if (raw === '' || raw == null) return fallback;
      const n = parseFloat(String(raw).replace(/[^\d.]/g, ''));
      return isNaN(n) ? fallback : n;
    };
    const minV = parse(this.minInput.value, 0);
    const maxV = parse(this.maxInput.value, this.sliderMax);
    this.minHandle.value = minV;
    this.maxHandle.value = maxV;
    this.paint(minV, maxV);
  }

  paint(minV, maxV) {
    const pctMin = this.sliderMax ? (minV / this.sliderMax) * 100 : 0;
    const pctMax = this.sliderMax ? (maxV / this.sliderMax) * 100 : 100;
    if (this.progress) {
      this.progress.style.left = pctMin + '%';
      this.progress.style.right = 100 - pctMax + '%';
    }
    if (this.minLabel) this.minLabel.textContent = formatMoneyFromFormat(minV, this.moneyFormat);
    if (this.maxLabel) this.maxLabel.textContent = formatMoneyFromFormat(maxV, this.moneyFormat);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
